import config from '../../config'
import axios from 'axios'

export type AutocompleteSuggestion = {
  address: string
  id: string
}

type IdealPostcodesAutocompleteHit = {
  id: string
  suggestion: string
}

type IdealPostcodesAutocompleteResponse = {
  code: number
  message: string
  result: {
    hits: IdealPostcodesAutocompleteHit[]
  }
}

type IdealPostcodesAddressResult = {
  line_1?: string
  line_2?: string
  line_3?: string
  post_town?: string
  postcode?: string
}

type IdealPostcodesAddressLookupResponse = {
  // Ideal Postcodes returns the resolved address object directly for this endpoint (per migration guide),
  // but we accept either shape for safety.
  code?: number
  message?: string
  result?: IdealPostcodesAddressResult
} & IdealPostcodesAddressResult

export type AutocompleteSuggestionDetails = {
  postcode: string
  town: string
  addressLine1: string
  addressLine2: string
}

class AddressService {
  private suggestionsCache: Record<string, AutocompleteSuggestion[]> = {}
  private suggestionDetailsCache: Record<string, AutocompleteSuggestionDetails> = {}

  autocomplete = async ({ address }: { address: string }): Promise<AutocompleteSuggestion[]> => {
    if (this.suggestionsCache[address]) {
      return this.suggestionsCache[address]
    }
    const res = await axios<IdealPostcodesAutocompleteResponse>({
      method: 'get',
      url: `https://api.ideal-postcodes.co.uk/v1/autocomplete/addresses`,
      params: {
        api_key: config.idealPostcodesApiKey,
        query: address,
      },
    })
    const suggestions: AutocompleteSuggestion[] = (res.data.result?.hits || []).map(hit => {
      const autocompleteSuggestion: AutocompleteSuggestion = {
        id: hit.id,
        address: hit.suggestion,
      }
      return autocompleteSuggestion
    })

    this.suggestionsCache[address] = suggestions
    return suggestions
  }
  fetchSuggestionDetailsById = async ({
    id,
  }: {
    id: string
  }): Promise<AutocompleteSuggestionDetails> => {
    if (this.suggestionDetailsCache[id]) {
      return this.suggestionDetailsCache[id]
    }
    const endpoint = `https://api.ideal-postcodes.co.uk/v1/autocomplete/addresses/${encodeURIComponent(
      id,
    )}/gbr`
    const res = await axios<IdealPostcodesAddressLookupResponse>({
      method: 'get',
      url: endpoint,
      params: {
        api_key: config.idealPostcodesApiKey,
      },
    })
    const data = res.data as unknown as {
      result?: IdealPostcodesAddressResult
    } & IdealPostcodesAddressResult
    const address = data.result ?? data
    const suggestionDetails = <AutocompleteSuggestionDetails>{
      addressLine1: [address.line_1, address.line_2].filter(Boolean).join(', '),
      addressLine2: address.line_3 ?? null,
      postcode: address.postcode ?? null,
      town: address.post_town ?? null,
    }
    this.suggestionDetailsCache[id] = suggestionDetails
    return suggestionDetails
  }
}

export default new AddressService()
